// Medical Portal — Report Detail modal
//
// Shown when a row in "My Reports" / Patient Reports is clicked.
// Test list comes from the visit row itself (already loaded by the
// parent page via the customer-sales API), so the modal opens
// instantly with no extra fetch. Each row + the top CTA hand off
// to IT Dose's hosted PDF endpoint in a new tab — a top-level
// navigation, so the browser sends the user's IT Dose session
// cookie and the WAF lets it through.
//
// Why we DON'T fetch JSON from IT Dose anymore:
//   First React request → no IT Dose cookie → Global.asax redirects
//   to Default.aspx → that 302 response carries no CORS header →
//   browser blocks the response. There's no client-side workaround;
//   the fix has to land in IT Dose's Global.asax. Opening PDFs as a
//   top-level navigation sidesteps this entirely.
import { useEffect, useMemo, useState } from 'react';
import { prefetchVisit } from '../../../utils/prefetchReports';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CollectionsBookmarkOutlinedIcon from '@mui/icons-material/CollectionsBookmarkOutlined';
import { MP, MONO, DISPLAY, cleanName } from './shared';
import QueryDetailsPanel, { statusOf } from './QueryDetailsPanel';
import { useAuth } from '../../../context/AuthContext';
import { useGetRcaInvestigationQuery } from '../../../store/queriesApi';
import {
  openItdoseReportPdf,
  logReportView,
} from '../../../utils/fetchItdoseReports';

// Readiness comes from the feed's `ReportReleased` flag (1 = the lab has
// signed the report off, 0 = still processing), carried onto each test by
// groupByVisit. `released === null` means the row didn't carry the field —
// treated as ready, which preserves the original behaviour for any source
// that doesn't populate it.
//
// This is what stops us promising a report that isn't there: previously every
// row rendered "Ready", the user clicked, and IT Dose hung for 12s before we
// could tell them it wasn't available. Now they know before clicking.
//
// Note `Status` is NOT usable for this — in this feed it carries priority
// words ("Normal"/"Urgent"), not approval state.
const isTestReady = (t) => !t?.is_hold && t?.released !== false;

const testStatusLabel = (t) => {
  if (t?.is_hold) return 'On hold';
  return t?.released === false ? 'Processing' : 'Ready';
};

const testDisplayName = (t) =>
  t?.name || t?.item_code || (t?.test_id ? `Test ${t.test_id}` : 'Test');

// Per-patient mock report payloads. If no entry exists, fall back to CBC + ESR demo.
const PATIENT_REPORTS = {
  'TL-P-2041': {
    name: 'Lakshmi Devi', uhid: 'UHID-2041', meta: 'F / 52 yrs',
    collected: '11 Apr 2026, 07:30 AM', reported: '09:14 AM',
    referredBy: 'Dr. Srinivas Rao', centre: 'TrustLab — HYD Centre',
    reportId: 'RPT-2026-9241', verifiedBy: 'Dr. S. Mehta (Pathologist)',
    panels: [
      {
        name: 'Complete Blood Count (CBC)',
        rows: [
          ['Haemoglobin',    '9.8',       'g/dL', '12.0 – 16.0',          'L', 'L'],
          ['WBC Count',      '7,400',     '/µL',  '4,000 – 11,000',        'N', 'N'],
          ['Platelet Count', '4,82,000',  '/µL',  '1,50,000 – 4,00,000',   'H', 'H'],
          ['MCV',            '68.2',      'fL',   '80 – 100',              'L', 'L'],
          ['MCH',            '21.4',      'pg',   '27 – 33',               'L', 'L'],
          ['Neutrophils',    '62',        '%',    '40 – 70',               'N', 'N'],
        ],
      },
      {
        name: 'Liver Function Test (LFT)',
        rows: [
          ['SGOT (AST)',          '42',  'U/L',   '< 40',       'H', 'H'],
          ['SGPT (ALT)',          '38',  'U/L',   '< 41',       'N', 'N'],
          ['Alkaline Phosphatase','98',  'U/L',   '40 – 129',   'N', 'N'],
          ['Total Bilirubin',     '1.4', 'mg/dL', '0.2 – 1.3',  'H', 'H'],
          ['Total Protein',       '6.9', 'g/dL',  '6.4 – 8.3',  'N', 'N'],
        ],
      },
    ],
    interpretation:
      'Findings suggest microcytic hypochromic anaemia, likely iron deficiency aetiology. Mild bilirubin elevation warrants clinical correlation. Suggest ferritin, serum iron, TIBC for confirmatory workup.',
  },
  'TL-P-2042': {
    name: 'Ravi Kumar', uhid: 'UHID-2042', meta: 'M / 48 yrs',
    collected: '11 Apr 2026, 08:15 AM', reported: '10:30 AM',
    referredBy: 'Dr. Srinivas Rao', centre: 'TrustLab — HYD Centre',
    reportId: 'RPT-2026-9242', verifiedBy: 'Dr. S. Mehta (Pathologist)',
    panels: [
      {
        name: 'Lipid Profile',
        rows: [
          ['Total Cholesterol', '218', 'mg/dL', '< 200',   'H', 'H'],
          ['HDL Cholesterol',   '38',  'mg/dL', '> 40',    'L', 'L'],
          ['LDL Cholesterol',   '148', 'mg/dL', '< 100',   'H', 'H'],
          ['Triglycerides',     '210', 'mg/dL', '< 150',   'H', 'H'],
          ['VLDL',              '32',  'mg/dL', '5 – 40',  'N', 'N'],
        ],
      },
      {
        name: 'HbA1c',
        rows: [
          ['Glycated Haemoglobin', '7.2', '%', '< 5.7 (normal)', 'H', 'H'],
        ],
      },
    ],
    interpretation:
      'Dyslipidaemia with elevated LDL and triglycerides; HDL is suboptimal. HbA1c indicates uncontrolled diabetes. Recommend lifestyle intervention, statin review, and endocrinology follow-up.',
  },
};

// Demo fallback so every TL-* id resolves to something.
const FALLBACK = {
  name: 'Patient', uhid: '—', meta: '—',
  collected: '—', reported: '—',
  referredBy: 'Dr. Srinivas Rao', centre: 'TrustLab — HYD Centre',
  reportId: '—', verifiedBy: 'Dr. S. Mehta (Pathologist)',
  panels: [{ name: 'Test panel', rows: [] }],
  interpretation: 'Report content unavailable for this mock patient.',
};

// `visit` carries the full visit object the row originated from — when
// the user taps "Raise Query" we forward it to onRaiseQuery so the
// parent dashboard can pre-fill the form. Falls back to undefined for
// callers that haven't been migrated yet (the form just won't pre-select).
const ReportDetailModal = ({ open, onClose, reportId, visit, onRaiseQuery }) => {
  const mock = PATIENT_REPORTS[reportId] || FALLBACK;
  // Prefer real `visit` data (from PatientReports / Overview rows)
  // for the header — mock values are only used as a soft fallback for
  // fields the visit row doesn't carry (gender/age, referring doctor,
  // collection centre, verifying pathologist).
  // When a real `visit` is present, drive the header off the API row —
  // the static `mock` values are only a fallback for the demo patient
  // IDs (TL-P-*) that don't originate from a real visit. The field
  // sources map to groupByVisit() in shared.js:
  //   referral → r.REFERALDOCTOR   ·   lab → r.TaggedLabName || r.PCCName
  const hasVisit = !!visit;
  const visitMeta = [visit?.gender, visit?.age ? `${visit.age} yrs` : null]
    .filter(Boolean)
    .join(' / ');
  const realReferral = visit?.referral && visit.referral !== '—'
    ? cleanName(visit.referral)
    : null;
  const data = {
    name:       visit?.patient || mock.name,
    uhid:       visit?.uhid    || mock.uhid,
    meta:       visitMeta || mock.meta,
    collected:  visit?.billDate || mock.collected,
    reported:   mock.reported,
    referredBy: realReferral || (hasVisit ? '—' : mock.referredBy),
    centre:     visit?.lab || (hasVisit ? '—' : mock.centre),
    reportId:   visit?.visitNo || mock.reportId,
    // The sales/billing feed carries no verifying-pathologist field, so
    // there's no dynamic source for this from the patient-reports API.
    verifiedBy: visit?.verifiedBy || (hasVisit ? '—' : mock.verifiedBy),
  };

  // Check whether a query already exists for this visit. We hit the
  // RCA detail endpoint by visitor_id (the visit number); a 200 with
  // a `record` means there's an open/closed query — render the
  // details panel inline + hide the "Raise Query" CTA. A 404 / empty
  // body means no query yet → keep the current "Raise Query" flow.
  const { user } = useAuth();
  const clientId = user?.client_id;
  const visitorId = visit?.visitNo;
  const skip = !open || !clientId || !visitorId;
  const { data: rcaData, isFetching: rcaFetching } = useGetRcaInvestigationQuery(
    { clientId, visitor_id: visitorId },
    { skip },
  );
  const existingQuery = rcaData?.record || null;
  const queryStatus = existingQuery ? statusOf(existingQuery) : null;
  // Hide the Raise Query CTA while we're checking too — avoids the
  // button briefly appearing then vanishing once the fetch resolves.
  const checkingQuery = !skip && rcaFetching && !rcaData;
  const hasOpenQuery = !!existingQuery;

  // ─── Test list (derived from the visit row, no extra fetch) ─────
  // The parent page (PatientReports / Overview) already loaded the
  // visit's test list from the customer-sales API. Map it into the
  // shape the row renderer expects. Every test renders as ready so
  // the user can always click through to the visit-level PDF.
  const pdfTests = useMemo(() => {
    const tests = Array.isArray(visit?.tests) ? visit.tests : [];
    return tests.map((t) => ({
      test_id: t?.id,         // serviceid (= lab ItemCode); used in audit log
      name: t?.name,
      department: t?.dept,
      released: t?.released,  // true / false / null (see isTestReady)
    }));
  }, [visit]);

  // Only released tests count. When NOTHING is released we hide the list and
  // show the "Reports under processing" state instead of offering buttons
  // that can only end in a timeout.
  const readyCount = useMemo(() => pdfTests.filter(isTestReady).length, [pdfTests]);
  const anyReady = readyCount > 0;
  // pdfLoading stays false because the test list comes from the parent
  // (pdfTests prop) and is already loaded by the time this modal
  // opens — there's no "fetching the list" phase.
  //
  // For per-click PDF downloads we use pendingPdfKey instead:
  //   • `'all'`         → the Open all-in-one PDF button is fetching
  //   • `<test.test_id>` → that specific row's PDF is fetching
  //   • `null`          → nothing pending
  //
  // The card / button being fetched shows an inline spinner so the
  // user has visible feedback during the proxy round-trip; the rest
  // of the list stays put.
  const pdfLoading = false;
  const [pdfError, setPdfError] = useState('');
  const [pendingPdfKey, setPendingPdfKey] = useState(null);

  // Warm the PDF the moment the modal opens — the user is almost
  // certainly going to click "Download visit PDF" within the next
  // few seconds. By the time their click fires, the cache is
  // (usually) hot and the new tab opens in <500ms.
  useEffect(() => {
    if (open && visitorId) prefetchVisit(visitorId);
  }, [open, visitorId]);

  // openItdoseReportPdf is now SYNCHRONOUS — it calls window.open in
  // the same call stack as the click handler. We deliberately don't
  // `await` it: any await inside an async function pops out into a
  // microtask, which the browser's popup blocker treats as "not a
  // user gesture" and refuses the new tab. Logging + state updates
  // run BEFORE the call so the row spinner flicks on for the few
  // hundred milliseconds the browser takes to spawn the tab.
  const openSingleTestPdf = (t) => {
    // serviceid from the sales feed is the lab's ItemCode (e.g. "TLBC001"),
    // not IT Dose's numeric Test_ID (e.g. 1372063). labreportnew.aspx only
    // accepts numeric Test_IDs, so passing an item code returns a blank
    // PDF. Until GetReports.aspx supports an itemCode → Test_ID lookup,
    // every row opens the visit-level PDF (all approved tests combined).
    if (!visitorId) return;
    if (pendingPdfKey) return;
    if (!isTestReady(t)) return; // row is already non-clickable; guard the call path too
    const key = t?.test_id || t?.item_code || 'row';
    logReportView({
      visitId: visitorId,
      itemCode: t?.test_id,
      source: 'medical_portal_modal_row',
    });
    setPdfError('');
    setPendingPdfKey(key);
    try {
      openItdoseReportPdf({ visitId: visitorId });
    } catch (err) {
      setPdfError(err?.message || 'Could not open the report.');
    }
    // Clear the spinner on the next tick — the new tab opens
    // synchronously, so this barely flashes.
    setTimeout(() => setPendingPdfKey(null), 50);
  };
  const openAllInOnePdf = () => {
    if (!visitorId) return;
    if (pendingPdfKey) return;
    logReportView({ visitId: visitorId, source: 'medical_portal_modal_visit' });
    setPdfError('');
    setPendingPdfKey('all');
    try {
      openItdoseReportPdf({ visitId: visitorId });
    } catch (err) {
      setPdfError(err?.message || 'Could not open the report.');
    }
    setTimeout(() => setPendingPdfKey(null), 50);
  };

  const handleClose = () => {
    onClose?.();
  };

  // Single-tap "Raise Query": close the dialog and let the parent
  // navigate. Passing `visit` so RaiseQuery can pre-select the
  // matching row in the patient picker without a second lookup.
  const handleRaiseQuery = () => {
    onClose?.();
    onRaiseQuery?.(visit);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { borderRadius: '6px', bgcolor: MP.white, maxHeight: '92vh' } }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 3,
          py: 2.25,
          borderBottom: `1px solid ${MP.border}`,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: DISPLAY, fontSize: 20, fontWeight: 700, letterSpacing: '-.01em', color: MP.dark, lineHeight: 1.2 }}>
            LAB REPORT · {data.name.toUpperCase()}
          </Typography>
          <Typography sx={{ fontSize: 12, color: MP.textMuted, mt: 0.5, fontFamily: MONO }}>
            <Box component="span" sx={{ color: MP.tealDark, fontWeight: 700 }}>{data.uhid}</Box>
            {' · '}{reportId}{' · '}{data.meta}{' · Collected: '}{data.collected}{' · Reported: '}{data.reported}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ flexShrink: 0 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* Info row — even 4-up grid on desktop, 2-up on mobile. Plain
            CSS grid avoids MUI Grid's negative-margin offset against this
            card's own padding, which was throwing the alignment off. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            columnGap: 2,
            rowGap: 1.5,
            bgcolor: MP.bg, borderRadius: '6px', px: 2.5, py: 2, mb: 2.5,
          }}
        >
          {[
            ['REFERRED BY', data.referredBy],
            ['COLLECTED AT', data.centre],
            ['REPORT ID', data.reportId],
            ['VERIFIED BY', data.verifiedBy],
          ].map(([k, v]) => (
            <Box key={k} sx={{ minWidth: 0 }}>
              <Typography sx={{
                fontSize: 10, color: MP.textMuted, fontFamily: MONO,
                letterSpacing: '0.1em', textTransform: 'uppercase',
              }}>
                {k}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: MP.dark, mt: 0.25 }}>
                {v}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* ─── PDF list (real IT Dose data) ─────────────────── */}
        <Box sx={{ mb: 1 }}>
          {pdfLoading && (
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ py: 4, justifyContent: 'center', color: MP.textMuted }}>
              <CircularProgress size={16} sx={{ color: MP.teal }} />
              <Typography sx={{ fontSize: 12, fontFamily: MONO, letterSpacing: '0.06em' }}>
                Fetching reports for visit {visitorId}…
              </Typography>
            </Stack>
          )}

          {!pdfLoading && pdfError && (
            <Box sx={{
              bgcolor: MP.redLight, border: `1px solid ${MP.red}`,
              borderRadius: '6px', px: 2, py: 1.75,
              color: MP.red, fontSize: 13,
            }}>
              <Typography sx={{ fontWeight: 700, fontSize: 12, mb: 0.5 }}>Couldn't load reports</Typography>
              <Typography sx={{ fontSize: 12 }}>{pdfError}</Typography>
            </Box>
          )}

          {!pdfLoading && !pdfError && pdfTests.length === 0 && (
            <Stack alignItems="center" spacing={1.25} sx={{ py: 5, color: MP.textMuted }}>
              <DescriptionOutlinedIcon sx={{ fontSize: 36, color: MP.textMuted, opacity: 0.7 }} />
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: MP.text }}>
                No reports filed for this visit yet
              </Typography>
              <Typography sx={{ fontSize: 12, color: MP.textMuted }}>
                Check back once the lab has uploaded the report.
              </Typography>
            </Stack>
          )}

          {!pdfLoading && !pdfError && pdfTests.length > 0 && !anyReady && (
            // "Processing" empty state — same copy as the mobile app's
            // ReportPdfListScreen. Hide the list when nothing's ready so
            // the user isn't taunted with rows they can't tap.
            <Stack alignItems="center" spacing={1.25} sx={{ py: 5, px: 2, textAlign: 'center' }}>
              <Box sx={{
                width: 64, height: 64, borderRadius: '50%',
                bgcolor: MP.tealLight, color: MP.tealDark,
                display: 'grid', placeItems: 'center',
              }}>
                <HourglassEmptyIcon sx={{ fontSize: 32 }} />
              </Box>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: MP.dark }}>
                Reports under processing
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: MP.textMuted, maxWidth: 400, lineHeight: 1.6 }}>
                The lab is still analysing the samples for this visit. Most reports
                are released within 6 hours of sample collection.
              </Typography>
            </Stack>
          )}

          {!pdfLoading && !pdfError && anyReady && (
            <>
              {/* "Open all-in-one" — IT Dose consolidates approved tests
                  for the visit into a single PDF when no testId is sent.
                  Shows an inline spinner while the proxy is fetching so
                  the user has visible feedback before the new tab opens. */}
              <Button
                fullWidth
                variant="contained"
                onClick={openAllInOnePdf}
                disabled={pendingPdfKey !== null}
                startIcon={
                  pendingPdfKey === 'all'
                    ? <CircularProgress size={16} sx={{ color: MP.white }} />
                    : <CollectionsBookmarkOutlinedIcon />
                }
                sx={{
                  bgcolor: MP.teal, color: MP.white,
                  textTransform: 'none', fontWeight: 700,
                  borderRadius: '6px', mb: 2,
                  '&:hover': { bgcolor: MP.tealDark, color: MP.white },
                  '&.Mui-disabled': { bgcolor: MP.tealLight, color: MP.tealDark, opacity: 0.85 },
                }}
              >
                {pendingPdfKey === 'all'
                  ? 'Preparing your PDF…'
                  : `Open all-in-one PDF · ${readyCount} of ${pdfTests.length} ready`}
              </Button>

              <Typography sx={{
                fontFamily: MONO, fontSize: 10.5,
                color: MP.textMuted, letterSpacing: '0.12em',
                textTransform: 'uppercase', mb: 1.25, ml: 0.5,
              }}>
                Or pick a single test
              </Typography>

              <Stack spacing={1}>
                {pdfTests.map((t, i) => {
                  const key = t.test_id || t.item_code || `row-${i}`;
                  return (
                    <TestPdfRow
                      key={key}
                      test={t}
                      onOpen={() => openSingleTestPdf(t)}
                      isLoading={pendingPdfKey === key}
                      disabled={pendingPdfKey !== null && pendingPdfKey !== key}
                    />
                  );
                })}
              </Stack>
            </>
          )}
        </Box>

        {/* Existing-query panel — shown when this visit already has
            an RCA record. Status tracker + incident grid + description
            + attachments + RCA findings. Hidden during the initial
            fetch so the user doesn't see a flash of "Raise Query" then
            a sudden swap. */}
        {hasOpenQuery && (
          <Box sx={{ mt: 3, pt: 3, borderTop: `2px solid ${MP.border}` }}>
            <QueryDetailsPanel
              record={existingQuery}
              isFetching={rcaFetching}
              showHeader
            />
          </Box>
        )}

        {/* Inline checking spinner — keeps the modal layout from
            re-flowing while we determine whether a query exists. */}
        {checkingQuery && (
          <Stack
            direction="row" alignItems="center" justifyContent="center"
            spacing={1} sx={{ mt: 3, py: 2, color: MP.textMuted }}
          >
            <CircularProgress size={14} sx={{ color: MP.teal }} />
            <Typography sx={{ fontSize: 12, fontFamily: MONO, letterSpacing: '0.06em' }}>
              Checking for an existing query…
            </Typography>
          </Stack>
        )}
      </DialogContent>

      {/* Footer — when no query exists yet, show "Raise Query" on the
          left so the doctor can act. When one already exists, that CTA
          is replaced with a quiet status hint pointing at the panel
          rendered inline above. Right side stays the same. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.25,
          px: 3, py: 2,
          borderTop: `1px solid ${MP.border}`,
        }}
      >
        {hasOpenQuery ? (
          <Typography
            sx={{
              fontSize: 12, color: MP.textMuted, fontFamily: MONO,
              letterSpacing: '0.04em',
            }}
          >
            Query already raised
            {existingQuery?.code && (
              <Box component="span" sx={{ color: MP.tealDark, fontWeight: 700 }}>
                {' · '}{existingQuery.code}
              </Box>
            )}
            {queryStatus && (
              <Box component="span" sx={{ color: MP.text }}>
                {' · '}{queryStatus}
              </Box>
            )}
          </Typography>
        ) : checkingQuery ? (
          <Box />
        ) : onRaiseQuery ? (
          // Only render the Raise-Query CTA when the parent supplied a
          // handler — the medical / lab portal does; the patient-facing
          // website dashboard doesn't (patients don't file RCAs).
          <Button
            variant="outlined" size="small"
            sx={{
              color: MP.red, borderColor: MP.red,
              textTransform: 'none', fontWeight: 600,
              '&:hover': { borderColor: MP.red, bgcolor: MP.redLight },
            }}
            onClick={handleRaiseQuery}
          >
            ⚠ Raise Query
          </Button>
        ) : (
          <Box />
        )}
        <Stack direction="row" spacing={1.25}>
          <Button
            variant="outlined" size="small"
            onClick={handleClose}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Close
          </Button>
          {anyReady && (
            <Button
              variant="contained" size="small"
              onClick={openAllInOnePdf}
              sx={{
                bgcolor: MP.teal, color: MP.white,
                textTransform: 'none', fontWeight: 700,
                '&:hover': { bgcolor: MP.tealDark, color: MP.white },
              }}
            >
              ⬇ Download visit PDF
            </Button>
          )}
        </Stack>
      </Box>
    </Dialog>
  );
};

// ─── PDF row — ready (green/clickable) or processing (gray/no-op) ───
const TestPdfRow = ({ test, onOpen, isLoading = false, disabled = false }) => {
  const ready    = isTestReady(test);
  const status   = testStatusLabel(test);
  // Clickable only if the row is ready, no other row is mid-fetch,
  // and this row isn't already loading. `isLoading` short-circuits
  // a second click while the first proxy call is in flight.
  const clickable = ready && !disabled && !isLoading;

  return (
    <Box
      onClick={clickable ? onOpen : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.75,
        py: 1.25,
        borderRadius: '6px',
        border: `1px solid ${isLoading ? MP.teal : MP.border}`,
        bgcolor: isLoading ? MP.tealLight : (ready ? MP.white : MP.bg),
        opacity: clickable ? 1 : 0.75,
        cursor: clickable ? 'pointer' : (isLoading ? 'wait' : 'not-allowed'),
        transition: 'all 0.15s',
        '&:hover': clickable ? { borderColor: MP.teal, bgcolor: MP.tealLight } : {},
      }}
    >
      {/* Icon tile — flips to a spinner while this row is fetching */}
      <Box sx={{
        width: 34, height: 34, borderRadius: '50%',
        bgcolor: isLoading ? MP.tealLight : (ready ? MP.greenLight : MP.bg),
        color:   isLoading ? MP.tealDark   : (ready ? MP.green      : MP.textMuted),
        display: 'grid', placeItems: 'center', flexShrink: 0,
      }}>
        {isLoading
          ? <CircularProgress size={16} sx={{ color: MP.tealDark }} />
          : <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />}
      </Box>

      {/* Name + department */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: 13, fontWeight: 600,
          color: ready ? MP.dark : MP.textMuted,
          lineHeight: 1.35,
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {testDisplayName(test)}
        </Typography>
        {test?.department && (
          <Typography sx={{ fontSize: 11.5, color: MP.textMuted, mt: 0.25 }}>
            {test.department}
          </Typography>
        )}
      </Box>

      {/* Status pill — shows "Preparing…" while this row is fetching */}
      <Box sx={{
        px: 1.25, py: 0.375, borderRadius: 100,
        bgcolor: isLoading ? MP.tealLight : (ready ? MP.greenLight : MP.bg),
        color:   isLoading ? MP.tealDark  : (ready ? MP.green      : MP.textMuted),
        fontFamily: MONO, fontSize: 10, fontWeight: 700,
        letterSpacing: '0.06em',
        minWidth: 78, textAlign: 'center',
        flexShrink: 0,
      }}>
        {isLoading ? 'Preparing…' : status}
      </Box>
    </Box>
  );
};

export default ReportDetailModal;
